'use client';

import * as React from 'react';
import { usePathname, useRouter } from 'next/navigation';
import posthog from 'posthog-js';
import { LocationPromptSheet, type LocationPromptState } from '@/features/location-prompt';
import '@/features/location-prompt/tokens';
import { useShareLocation } from '@/api/mutations/profile.mutations';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDeviceOS } from '@/hooks/use-device-os';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import {
  APP_BADGE_CLOSED_EVENT,
  APP_BADGE_DISMISS_COOKIE,
  LOCATION_SHARED_COOKIE,
  LOCATION_DISMISSED_COOKIE,
  LOCATION_SHARED_MAX_AGE,
  LOCATION_DISMISSED_MAX_AGE,
  hasCookie,
  isPlaybackRoute,
  useWhatsNewHoldsPromptSlot,
  writeCookie,
} from './prompt-coordination';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useTourActive } from '@/shared/lib/tours/tour-store';

const SHOW_DELAY_MS = 2500;
const TERMINAL_HIDE_MS = 4000; // auto-dismiss denied / error
const MAX_LOCATION_ATTEMPTS = 3;

const IDLE_COPY = {
  title: 'Show up on the globe',
  sub: 'Share your approximate location to appear in the live globe alongside everyone watching right now.',
};

const GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 10_000,
  maximumAge: 600_000,
};

type HostState = LocationPromptState | 'hidden';

/**
 * Location opt-in prompt host (FRONT-218). Mounts the closed-world
 * `<LocationPromptSheet>` when every gate passes:
 *   1. Server sharing state is known (`user.location` from /me) and off, no
 *      `tt_location_shared` cookie, and not settled or ignored this session
 *   2. Pathname is not a playback route
 *   3. On mobile: the app-install badge has already been closed (no stacking)
 *   4. No `tt_location_dismissed` cookie (dismissal / denial hold for 7 days
 *      on every route, `/globe` included — prompt once, then honor the cooldown)
 *   5. What's New is not deciding or showing (shared prompt slot)
 *   6. A 2.5s delay after gates 1–5 settle
 *
 * On "Share location" → browser geolocation (the click is the required user
 * gesture) → POST /me/location. Denial / error / 422 physics-violation / 429
 * throttle all write the dismiss cookie and never retry. Mounted only inside
 * the authenticated branch of (platform)/layout.tsx.
 */
export function LocationPromptHost() {
  const pathname = usePathname();
  const router = useRouter();
  const isMobile = useIsMobile();
  const os = useDeviceOS();
  const { mutate } = useShareLocation();
  // Authoritative sharing state from /me (BE owner-only field) — suppress the
  // prompt for users already sharing even if their cookie was cleared. Absent
  // means unknown, not "off": session-creating responses (login, 2FA, Whop,
  // checkout) omit `location`, and reading that as "off" re-prompted users who
  // had already shared, right after every sign-in.
  const userId = useAuthStore((s) => s.user?.id);
  const serverLocation = useAuthStore((s) => s.user?.location);
  const locationKnown = serverLocation != null;
  const userSharing = serverLocation?.share_location ?? false;
  const whatsNewHoldsSlot = useWhatsNewHoldsPromptSlot();
  const tourActive = useTourActive();

  const [state, setState] = React.useState<HostState>('hidden');
  const [shared, setShared] = React.useState(false); // tt_location_shared cookie — sticky opt-in
  const [dismissedCookie, setDismissedCookie] = React.useState(false); // tt_location_dismissed — 7d cooldown
  const [settled, setSettled] = React.useState(false); // acted this session — never re-arm until reload
  const [ignored, setIgnored] = React.useState(false); // navigated away from an open sheet — not again until reload
  const [appBadgeDismissed, setAppBadgeDismissed] = React.useState(false);
  const shownRef = React.useRef(false);
  const locationAttempts = React.useRef<{
    userId: number;
    count: number;
    busy: boolean;
    done: boolean;
  } | null>(null);
  const timers = React.useRef<number[]>([]);

  const clearTimers = React.useCallback(() => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  }, []);

  React.useEffect(() => {
    setShared(hasCookie(LOCATION_SHARED_COOKIE));
    setDismissedCookie(hasCookie(LOCATION_DISMISSED_COOKIE));
    setAppBadgeDismissed(hasCookie(APP_BADGE_DISMISS_COOKIE));
  }, []);

  // The app badge owns the bottom slot first on mobile; unblock when it closes.
  React.useEffect(() => {
    const onBadgeClosed = () => setAppBadgeDismissed(true);
    window.addEventListener(APP_BADGE_CLOSED_EVENT, onBadgeClosed);
    return () => window.removeEventListener(APP_BADGE_CLOSED_EVENT, onBadgeClosed);
  }, []);

  // Retries ride on navigation (pathname dep) rather than a timer, capped so a
  // flaky /me can't turn every route change into a request.
  React.useEffect(() => {
    if (userId === undefined || locationKnown) return;
    const prev = locationAttempts.current;
    const attempts =
      prev?.userId === userId ? prev : { userId, count: 0, busy: false, done: false };
    if (attempts.busy || attempts.done || attempts.count >= MAX_LOCATION_ATTEMPTS) return;
    attempts.count += 1;
    attempts.busy = true;
    locationAttempts.current = attempts;
    void useAuthStore
      .getState()
      .refreshUserLocation()
      .then((settled) => {
        attempts.busy = false;
        attempts.done = settled;
      });
  }, [userId, locationKnown, pathname]);

  // Client-side navigation lets a fresh route re-evaluate eligibility — the host
  // never remounts, so without this a prompt held back by a playback route or
  // the app badge would wait for a hard reload. The dismiss cookie still
  // suppresses re-prompts on every route. A sheet left untouched while the user
  // navigates is an answer too: hide it for the rest of the session instead of
  // carrying it (and a fresh `location_prompt_shown`) onto every route.
  const prevPathRef = React.useRef(pathname);
  React.useEffect(() => {
    if (pathname === prevPathRef.current) return;
    prevPathRef.current = pathname;
    if (state === 'idle') {
      clearTimers();
      setIgnored(true);
      setState('hidden');
      return;
    }
    setSettled(false);
    shownRef.current = false;
  }, [pathname, state, clearTimers]);

  React.useEffect(() => clearTimers, [clearTimers]);

  const suppressed = typeof pathname === 'string' ? isPlaybackRoute(pathname) : true;
  const appBadgePending = isMobile && os !== null && !appBadgeDismissed;
  const eligible =
    locationKnown &&
    !shared &&
    !userSharing &&
    !settled &&
    !ignored &&
    !suppressed &&
    !appBadgePending &&
    !dismissedCookie &&
    !whatsNewHoldsSlot &&
    !tourActive;

  // Arm the entry delay only from the hidden state; once asking/terminal, leave it.
  React.useEffect(() => {
    if (!eligible || state !== 'hidden') return;
    const timer = window.setTimeout(() => setState('idle'), SHOW_DELAY_MS);
    timers.current.push(timer);
    return () => window.clearTimeout(timer);
  }, [eligible, state, pathname]);

  React.useEffect(() => {
    if (state === 'idle' && !shownRef.current) {
      shownRef.current = true;
      posthog.capture(AnalyticsEvent.LOCATION_PROMPT_SHOWN, { pathname });
    }
  }, [state, pathname]);

  const scheduleHide = React.useCallback((ms: number) => {
    timers.current.push(window.setTimeout(() => setState('hidden'), ms));
  }, []);

  const fail = React.useCallback(
    (errorType: string) => {
      posthog.capture(AnalyticsEvent.LOCATION_SHARE_FAILED, { pathname, error_type: errorType });
      writeCookie(LOCATION_DISMISSED_COOKIE, LOCATION_DISMISSED_MAX_AGE);
      setDismissedCookie(true);
      setSettled(true);
      setState('error');
      scheduleHide(TERMINAL_HIDE_MS);
    },
    [pathname, scheduleHide]
  );

  const onGeoSuccess = React.useCallback(
    (pos: GeolocationPosition) => {
      mutate(
        {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          share_location: true,
        },
        {
          onSuccess: () => {
            posthog.capture(AnalyticsEvent.LOCATION_SHARED, { pathname });
            writeCookie(LOCATION_SHARED_COOKIE, LOCATION_SHARED_MAX_AGE);
            setShared(true);
            setSettled(true);
            setState('success');
          },
          onError: () => fail('post'),
        }
      );
    },
    [mutate, pathname, fail]
  );

  const onGeoError = React.useCallback(
    (err: GeolocationPositionError) => {
      if (err.code === err.PERMISSION_DENIED) {
        posthog.capture(AnalyticsEvent.LOCATION_PERMISSION_DENIED, { pathname });
        writeCookie(LOCATION_DISMISSED_COOKIE, LOCATION_DISMISSED_MAX_AGE);
        setDismissedCookie(true);
        setSettled(true);
        setState('denied');
        scheduleHide(TERMINAL_HIDE_MS);
        return;
      }
      fail(err.code === err.TIMEOUT ? 'timeout' : 'position_unavailable');
    },
    [pathname, scheduleHide, fail]
  );

  const handleShare = React.useCallback(() => {
    posthog.capture(AnalyticsEvent.LOCATION_PROMPT_ALLOWED, { pathname });
    clearTimers();
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      fail('unsupported');
      return;
    }
    setState('requesting');
    // Intentional, opt-in geolocation: only fires from the user's "Share location"
    // click (a real gesture, secure context), shown once with a remembered
    // dismissal — the consented capture FRONT-218 exists to do. SonarJS flags
    // this (no-intrusive-permissions) by design; the gesture + consent is the answer.
    navigator.geolocation.getCurrentPosition(onGeoSuccess, onGeoError, GEO_OPTIONS);
  }, [pathname, clearTimers, fail, onGeoSuccess, onGeoError]);

  const handleDismiss = React.useCallback(() => {
    clearTimers();
    if (state !== 'success') {
      writeCookie(LOCATION_DISMISSED_COOKIE, LOCATION_DISMISSED_MAX_AGE);
      setDismissedCookie(true);
    }
    if (state === 'idle') posthog.capture(AnalyticsEvent.LOCATION_PROMPT_DISMISSED, { pathname });
    setSettled(true);
    setState('hidden');
  }, [clearTimers, pathname, state]);

  const handleGoGlobe = React.useCallback(() => {
    clearTimers();
    setState('hidden');
    router.push('/globe');
  }, [clearTimers, router]);

  if (state === 'hidden') return null;

  return (
    <div className="tlp-layer" data-tone="dark" aria-live="polite">
      <LocationPromptSheet
        state={state}
        copy={IDLE_COPY}
        onShare={handleShare}
        onDismiss={handleDismiss}
        onGoGlobe={handleGoGlobe}
      />
    </div>
  );
}
